/**
 * Boot-time dark mode sync — must run BEFORE ChakraProvider mounts.
 *
 * Dark mode lives in two systems that must always agree:
 *   1. `themeMode` key + `dark` class on <body> → Tailwind / index.css tokens
 *   2. `chakra-ui-color-mode` key → Chakra colorMode (global text/border colors)
 * Each is restored from its own localStorage key on refresh, so if they ever
 * drift the UI hydrates half-dark/half-light. Resolving ONE mode here and
 * writing it to both keys (plus the body class) before React renders makes
 * every consumer — including components that read colorMode only at mount —
 * start from the same value.
 */
export type ThemeMode = "light" | "dark";

export function syncThemeModeAtBoot(): ThemeMode {
  let mode: ThemeMode = "light";
  try {
    const stored =
      localStorage.getItem("themeMode") ||
      localStorage.getItem("chakra-ui-color-mode");
    if (stored === "dark" || stored === "light") {
      mode = stored;
    } else if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      mode = "dark";
    }
    localStorage.setItem("themeMode", mode);
    localStorage.setItem("chakra-ui-color-mode", mode);
  } catch {
    // localStorage unavailable (private mode) — fall through with default
  }
  document.body.classList.toggle("dark", mode === "dark");
  return mode;
}
